import React, { useState, useEffect, useRef } from 'react';
import { getVideoBanners, deleteVideoBanner, updateVideoBannersSort } from '../../services/adminApi';
import VideoBannerForm from './VideoBannerForm';
import './VideoBannersList.css';

const VideoBannersList = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const data = await getVideoBanners();
      // Sort by SORT field - 500 should be last
      const sorted = [...data].sort((a, b) => {
        const aSort = a.SORT || 500;
        const bSort = b.SORT || 500;
        // If both are 500, sort by ID
        if (aSort === 500 && bSort === 500) {
          return b.ID - a.ID;
        }
        // If one is 500, it goes last
        if (aSort === 500) return 1;
        if (bSort === 500) return -1;
        // Otherwise sort by SORT value
        return aSort - bSort;
      });
      setBanners(sorted);
    } catch (error) {
      console.error('Error loading video banners:', error);
      alert('Не удалось загрузить видео баннеры');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedBanners = [...banners].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'ACTIVE') {
      aValue = a.ACTIVE === 'Y' ? 1 : 0;
      bValue = b.ACTIVE === 'Y' ? 1 : 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
    }
    if (typeof bValue === 'string') {
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот видео баннер?')) {
      return;
    }

    try {
      await deleteVideoBanner(id);
      loadBanners();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting video banner:', error);
      alert('Не удалось удалить видео баннер');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(sortedBanners.map(b => b.ID)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!window.confirm(`Вы уверены, что хотите удалить ${count} ${count === 1 ? 'видео баннер' : count < 5 ? 'видео баннера' : 'видео баннеров'}?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      // Delete all selected items
      const deletePromises = Array.from(selectedIds).map(id => deleteVideoBanner(id));
      await Promise.all(deletePromises);
      setSelectedIds(new Set());
      loadBanners();
    } catch (error) {
      console.error('Error bulk deleting video banners:', error);
      alert('Не удалось удалить выбранные видео баннеры');
    } finally {
      setIsDeleting(false);
    }
  };

  const isAllSelected = sortedBanners.length > 0 && selectedIds.size === sortedBanners.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < sortedBanners.length;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingBanner(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingBanner(null);
    loadBanners();
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newBanners = [...banners];
    const draggedItem = newBanners[draggedIndex];
    newBanners.splice(draggedIndex, 1);
    newBanners.splice(dropIndex, 0, draggedItem);

    // Update sort values
    const updatedItems = newBanners.map((banner, index) => ({
      id: banner.ID,
      sort: (index + 1) * 10 // Increment by 10 for easier reordering
    }));

    try {
      await updateVideoBannersSort(updatedItems);
      setBanners(newBanners);
    } catch (error) {
      console.error('Error updating sort order:', error);
      alert('Не удалось обновить порядок сортировки');
      loadBanners(); // Reload on error
    }

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (path.startsWith('/upload')) {
      return `${process.env.REACT_APP_BACKEND_URL || 'https://www.kapussta.com'}${path}`;
    }
    return path;
  };

  if (loading) {
    return <div className="loading">Загрузка видео баннеров...</div>;
  }

  return (
    <div className="video-banners-list">
      <div className="list-header">
        <h2>Видео Баннеры</h2>
        <div className="header-actions">
          {selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete} 
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаление...' : `Удалить выбранные (${selectedIds.size})`}
            </button>
          )}
          <button onClick={handleCreate} className="btn-primary">+ Добавить видео баннер</button>
        </div>
      </div>

      {showForm && (
        <VideoBannerForm
          banner={editingBanner}
          onClose={handleFormClose}
        />
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <input
                  type="checkbox"
                  ref={selectAllCheckboxRef}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  title="Выбрать все"
                />
              </th>
              <th style={{ width: '50px' }}>Порядок</th>
              <th>ID</th>
              <th 
                className="sortable" 
                onClick={() => handleSort('NAME')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Название {getSortIcon('NAME')}
              </th>
              <th>Видео/Изображение</th>
              <th>Ссылка</th>
              <th 
                className="sortable" 
                onClick={() => handleSort('ACTIVE')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Активен {getSortIcon('ACTIVE')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('SORT')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Сортировка {getSortIcon('SORT')}
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedBanners.map((banner, index) => (
              <tr 
                key={banner.ID}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`${draggedIndex === index ? 'dragging' : ''} ${selectedIds.has(banner.ID) ? 'selected' : ''}`}
                style={{ cursor: 'move' }}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(banner.ID)}
                    onChange={() => handleSelectItem(banner.ID)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>
                  <span className="drag-handle">⋮⋮</span>
                </td>
                <td>{banner.ID}</td>
                <td>{banner.NAME || '(без названия)'}</td>
                <td>
                  {banner.VIDEO_PATH && (
                    <div className="media-preview">
                      {banner.IS_VIDEO ? (
                        <video 
                          src={getImageUrl(banner.VIDEO_PATH)}
                          className="thumbnail"
                          muted
                          playsInline
                        />
                      ) : (
                        <img 
                          src={getImageUrl(banner.VIDEO_PATH)}
                          alt={banner.NAME}
                          className="thumbnail"
                        />
                      )}
                    </div>
                  )}
                </td>
                <td>
                  {banner.LINK ? (
                    <a href={banner.LINK} target="_blank" rel="noopener noreferrer" className="link-preview">
                      {banner.LINK.length > 40 ? banner.LINK.substring(0, 40) + '...' : banner.LINK}
                    </a>
                  ) : (
                    <span className="no-link">—</span>
                  )}
                </td>
                <td>
                  <span className={`status ${banner.ACTIVE === 'Y' ? 'active' : 'inactive'}`}>
                    {banner.ACTIVE === 'Y' ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td>{banner.SORT || 500}</td>
                <td>
                  <button onClick={() => handleEdit(banner)} className="btn-edit">Редактировать</button>
                  <button onClick={() => handleDelete(banner.ID)} className="btn-delete">Удалить</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VideoBannersList;

